/**
 * Commit-on-blur/Enter numeric input state for a threshold override row.
 *
 * Typing is free-form (so "-", "1.", a cleared box are all fine mid-edit) and
 * only a parsed number is committed upward. Empty or "-" commits 0; anything
 * unparseable reverts to the last committed value rather than writing NaN.
 *
 * Shared by the country and campaign override rows.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';

export interface SyncedThresholdInput {
  display: string;
  onChange: (raw: string) => void;
  onBlur: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
}

export function useSyncedThresholdInput(
  committedValue: number,
  onCommit: (n: number) => void,
): SyncedThresholdInput {
  const lastCommitted = useRef(committedValue);
  const [display, setDisplay] = useState(() => String(committedValue));

  useEffect(() => {
    if (lastCommitted.current !== committedValue) {
      lastCommitted.current = committedValue;
      setDisplay(String(committedValue));
    }
  }, [committedValue]);

  const flush = useCallback(() => {
    const t = display.trim();
    if (t === '' || t === '-') {
      lastCommitted.current = 0;
      onCommit(0);
      setDisplay('0');
      return;
    }
    const n = Number(t);
    if (Number.isNaN(n)) {
      setDisplay(String(lastCommitted.current));
      return;
    }
    lastCommitted.current = n;
    onCommit(n);
    setDisplay(String(n));
  }, [display, onCommit]);

  const onChange = useCallback((raw: string) => setDisplay(raw), []);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        flush();
      }
    },
    [flush],
  );

  return { display, onChange, onBlur: flush, onKeyDown };
}
